import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Card, CheckboxLabel, ContentCard, Filter, FilterSetController, Icon, Status, Table, TooltipHelp, Tab, ToastContext } from '@weezevent/nacre';
import { ModerationFilesManagement } from '../../../../models';
import { exportFilesStatusType, exportStatusFilterOptions } from '../../../../constants/moderation';

import { handleSearchQuery } from '../../../manager/components/context';
import { useCurrentPageFilters } from '../../../../utils/filters';
import { quickFilter } from '../../../../utils/tools';

import LoadingTable from '../../../../components/loadingTable';
import { DateInTable } from '../../components/DateInTable';

import useModerationContext from '../../../../context/useModerationContext';
import DownloadFileModal from './components/downloadFileModal';
import ModerationPaginator from '../../components/moderationPaginator';

import Styles from './filesManagement.module.css';

const spec = {
    multiple: ['status'],
    fields: ['search'],
    aliases: {},
    date: [],
    form: []
};

export default function ExportsTab({ organization, flowId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [currentPage, setCurrentPage] = React.useState(0);
    const [, setModerationContext] = useModerationContext();
    const toast = React.useContext(ToastContext);
    const [pageSize] = React.useState(50);
    const [filters, setFilters] = useCurrentPageFilters(spec);
    const defaultFilters = {};
    const prefix = React.useMemo(() => `/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/files`, [flowId, organization]);

    const [files, loadingFiles, syncFiles] = ModerationFilesManagement.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            launch: flowId,
            cache: false,
            allow_cache: false,
            query: {
                offset: currentPage * pageSize,
                limit: pageSize,
                ...handleSearchQuery(spec, filters)
            },
            onSuccess: res => {
                setModerationContext({ syncActiveTab: syncFiles, download_all_enable: res.some(file => file.status === 'ready') });
            }
        },
        [organization, flowId, filters, pageSize, currentPage]
    );

    const handleSearch = React.useCallback(
        value => {
            setFilters({ ...filters, search: value });
        },
        [filters]
    );

    const handleQuickFilters = React.useCallback(
        (name, checked) => {
            setFilters(quickFilter(filters, 'status', name, checked));
        },
        [filters]
    );

    const handleDownloadFile = React.useCallback(
        fileId => {
            ModerationFilesManagement.downloadFile({
                organizationId: organization.id,
                flowId,
                fileId
            })
                .then(res => {
                    const blob = new Blob([res.response], { type: 'text/csv' });
                    const file = window.URL.createObjectURL(blob);
                    window.open(file, '_blank');
                    syncFiles();
                })
                .catch(e => {
                    toast.error(t('ticket.moderation.filesManagement.downloadError'));
                });
        },
        [organization, flowId, syncFiles]
    );

    return (
        <Tab
            panes={[
                {
                    id: 0,
                    menuItem: t('ticket.moderation.filesManagement.exportsTab'),
                    menuIcon: 'File-export',
                    url: `${prefix}/exports`
                },
                {
                    id: 1,
                    menuItem: t('ticket.moderation.filesManagement.importsTab'),
                    menuIcon: 'upload',
                    url: `${prefix}/imports`
                }
            ]}
            activeTab={0}
            onTabSelected={tab => {
                setFilters(defaultFilters);
                history.push(tab.url);
            }}
        >
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={
                            // We get an empty response when the list is empty,
                            // so the `|| 0` is justified here
                            files?.__meta?.headers?.get('total-count') || 0
                        }
                        isLoadingTotal={loadingFiles}
                        labelTotal={t('ticket.moderation.filesManagement.table.countTotal', { count: files?.__meta?.headers?.get('total-count') || 0 })}
                        onChange={values => setFilters(values)}
                        initialValues={filters}
                        defaultValues={defaultFilters}
                        onReset={() => {}}
                        search={{
                            onChange: (_, search = '') => handleSearch(search),
                            handleReset: () => setFilters(filters => ({ ...filters, search: '' })),
                            placeholder: t('ticket.moderation.common.searchPlaceholder'),
                            value: filters?.search
                        }}
                    >
                        <Filter name="status" labelName={t('ticket.moderation.filesManagement.table.filterStatus')} multiple options={exportStatusFilterOptions()} index="1" />
                    </FilterSetController>
                    <div className={Styles['filesManagement-subfilters-container']}>
                        <div className={Styles['filesManagement-subfilters-quickfilter-container']}>
                            <span>{t('ticket.moderation.filesManagement.table.quickFilter')}</span>
                            <CheckboxLabel
                                className={Styles['filesManagement-subfilters-quickfilter-checkbox']}
                                label={t('ticket.moderation.filesManagement.table.filesReady')}
                                checked={filters.status === 'ready' || filters.status?.has('ready')}
                                onClick={({ target: { checked } }) => handleQuickFilters('ready', checked)}
                            />
                        </div>
                    </div>
                </ContentCard>
                <Table useNew>
                    <Table.Header>
                        <Table.Tr>
                            <th>{t('ticket.moderation.filesManagement.table.date')}</th>
                            <th>{t('ticket.moderation.filesManagement.table.file')}</th>
                            <th>{t('ticket.moderation.filesManagement.table.attendees')}</th>
                            <th>{t('ticket.moderation.filesManagement.table.downloads')}</th>
                            <th>
                                {t('ticket.moderation.filesManagement.table.updates')}
                                <TooltipHelp title={t('ticket.moderation.filesManagement.table.updatesTooltip')} />
                            </th>
                            <th>{t('ticket.moderation.filesManagement.table.status')}</th>
                            <th>{t('ticket.moderation.filesManagement.table.actions')}</th>
                        </Table.Tr>
                    </Table.Header>
                    {!!files?.length && !loadingFiles && (
                        <Table.TBody>
                            {files?.map(file => (
                                <FilesRow key={file.id} file={file} organization={organization} flowId={flowId} handleDownloadFile={handleDownloadFile} />
                            ))}
                        </Table.TBody>
                    )}
                </Table>
                <LoadingTable loading={loadingFiles} height="30vh" />
                {!loadingFiles && !files?.length && <Card.EmptyState title={t('ticket.moderation.filesManagement.emptyTitle')} icon={<Icon name="list" />} />}
                <ModerationPaginator currentPage={currentPage} setCurrentPage={setCurrentPage} totalCount={+files?.__meta?.headers?.get('total-count') || 0} pageSize={pageSize} />
            </Card>
        </Tab>
    );
}

const FilesRow = ({ file, organization, handleDownloadFile }) => {
    const { t } = useTranslation();
    const [openDownloadFileModal, setOpenDownloadFileModal] = React.useState(false);

    return (
        <>
            <Table.Tr>
                <Table.Td>
                    <DateInTable date={file.created} timezone={organization.timezone} language={organization.default_locale} />
                </Table.Td>
                <Table.Td>{file.filename}</Table.Td>
                <Table.Td>
                    {file.count_attendees} / {file.max_attendees}
                </Table.Td>
                <Table.Td>{file.count_downloads}</Table.Td>
                <Table.Td></Table.Td>
                <Table.Td>
                    <Status color={exportFilesStatusType[file.status]} text={t(`ticket.moderation.common.status.${file.status}`)} />
                </Table.Td>
                <Table.Td>
                    <Icon
                        name="download"
                        onClick={() => {
                            if (file.status === 'building' || file.count_downloads > 0) {
                                setOpenDownloadFileModal(true);
                            } else {
                                handleDownloadFile(file.id);
                            }
                        }}
                    />
                </Table.Td>
            </Table.Tr>
            <DownloadFileModal open={openDownloadFileModal} setOpenModal={setOpenDownloadFileModal} file={file} handleDownloadFile={handleDownloadFile} />
        </>
    );
};
